// frontend/src/components/EnduranceAddModal.js
// Modal component for adding endurance data directly in the flowsheet

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Row,
  Col,
  Input,
  Space,
  Typography,
  Alert,
  message,
  Divider
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { usePatientChart } from '../context/PatientChartContext';
import MomentDatePicker from './MomentDatePicker.tsx';

const { Title, Text } = Typography;

/**
 * EnduranceAddModal Component
 * Provides a dedicated interface for adding endurance data to the flowsheet
 */
const EnduranceAddModal = ({
  visible,
  onClose,
  onSave,
  patientId,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(moment());
  const { refreshVitals } = usePatientChart();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedDateTime(moment());
    }
  }, [visible, form]);

  // Handle form submission
  const handleSave = async () => {
    try {
      setSubmitLoading(true);
      
      // Validate form fields
      const values = await form.validateFields();
      
      // Filter out empty values
      const filteredValues = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
          filteredValues[key] = values[key];
        }
      });

      if (Object.keys(filteredValues).length === 0) {
        message.warning('Please enter at least one endurance parameter.');
        return;
      }

      // Prepare payload for backend - using the biometrics endpoint
      const payload = {
        entryDate: selectedDateTime.toISOString(),
        biometrics: filteredValues
      };

      // Call backend API using the existing biometrics endpoint
      const response = await axios.post(`/patients/${patientId}/flowsheet/biometrics`, payload);

      if (response.data.status === 'success') {
        message.success('Endurance data saved successfully!');

        // Refresh vitals banner (in case endurance data affects displayed metrics)
        refreshVitals();

        // Call parent onSave callback to refresh flowsheet
        if (onSave) {
          onSave();
        }

        // Close modal and reset form
        onClose();
        form.resetFields();
      } else {
        throw new Error(response.data.message || 'Failed to save endurance data');
      }
    } catch (error) {
      console.error('Error saving endurance data:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to save endurance data');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Define endurance parameters based on parameters.md
  const enduranceFields = [
    { name: 'biometrics_endurance_wallsit_duration', label: 'Wall Sit Duration', units: 'seconds', inputType: 'InputNumber', placeholder: 'seconds', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_endurance_pushups_completed', label: 'Push-ups Completed', inputType: 'InputNumber', placeholder: 'count', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_endurance_step_test_duration', label: 'Step Test Duration', units: 'minutes', inputType: 'InputNumber', placeholder: 'minutes', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_endurance_heart_rate_peak', label: 'Peak Heart Rate', units: 'bpm', inputType: 'InputNumber', placeholder: 'bpm', rules: [{ type: 'number', min: 0, max: 250, message: 'Heart rate must be between 0 and 250 bpm' }] },
    { name: 'biometrics_endurance_heart_rate_1min', label: 'Heart Rate at 1 Minute', units: 'bpm', inputType: 'InputNumber', placeholder: 'bpm', rules: [{ type: 'number', min: 0, max: 250, message: 'Heart rate must be between 0 and 250 bpm' }] },
    { name: 'biometrics_endurance_heart_rate_1min_post_exercise', label: 'Heart Rate 1 Min Post-Exercise', units: 'bpm', inputType: 'InputNumber', placeholder: 'bpm', rules: [{ type: 'number', min: 0, max: 250, message: 'Heart rate must be between 0 and 250 bpm' }] },
  ];

  return (
    <Modal
      title={
        <Space>
          <ClockCircleOutlined />
          Add Endurance Data
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={submitLoading}
          onClick={handleSave}
          style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
        >
          Save Endurance Data
        </Button>,
      ]}
      width={800}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Add New Endurance Entry"
          description="Enter cardiovascular endurance test results and aerobic capacity measurements for the selected date and time. These assessments help track cardiovascular fitness and endurance changes over time."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {/* Date/Time Selection */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Text strong>Entry Date & Time:</Text>
            <br />
            <MomentDatePicker
              showTime
              value={selectedDateTime}
              onChange={setSelectedDateTime}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select date and time"
            />
          </Col>
        </Row>
      </div>

      <Form form={form} layout="vertical" name="endurance_add_form">
        <Title level={5}>Endurance Assessment Parameters</Title>
        
        {/* Endurance Tests */}
        <Divider orientation="left">Endurance Tests</Divider>
        <Row gutter={16}>
          {enduranceFields.slice(0, 3).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  placeholder={field.placeholder}
                  min={field.rules?.[0]?.min}
                  max={field.rules?.[0]?.max}
                  precision={1}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Heart Rate Measurements */}
        <Divider orientation="left">Heart Rate Measurements</Divider>
        <Row gutter={16}>
          {enduranceFields.slice(3).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  placeholder={field.placeholder}
                  min={field.rules?.[0]?.min}
                  max={field.rules?.[0]?.max}
                  precision={0}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Form>
    </Modal>
  );
};

export default EnduranceAddModal;
